import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, Calendar, Check, FileText, User,
    X as XIcon, ExternalLink, ClipboardList,
    Banknote, Loader2, CheckCircle2, XCircle, Clock, Layers, Wallet,
    RotateCcw, AlertTriangle, Edit3, Save, Users, FileStack, Trash2,
    MapPin, Phone, Mail, ChevronRight, Building2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../store/auth';
import { useSettings } from '../../store/settings';
import { submissionFinancePreview } from '../../utils/daewooFinance';
import { uploadHref } from '../../utils/uploadHref';
import RefundRequestModal from '../../components/RefundRequestModal';
import ClientChangeRequestModal from '../../components/ClientChangeRequestModal';
import LoiProgressSection from '../../components/LoiProgressSection';
import { canRequestClientChangeOnSubmission } from '../../constants/clientAccess';
import { ADMIN_ROLE } from '../../config/roles';
import { canUpdateLoiStage, canUpdateAgreementStage } from '../../utils/workspace';
import { useRoleDoc } from '../../utils/useRoleDoc';

const inr = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(v || 0));
const inr2 = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(Number(v || 0));
const fd = (d) => {
    if (!d) return '—';
    const p = new Date(d);
    return isNaN(p) ? '—' : p.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const short = (id) => (id ? id.slice(-6).toUpperCase() : '—');

const STATUS_CFG = {
    submitted: { label: 'Pending review', ring: 'ring-amber-200', bg: 'bg-amber-50', text: 'text-amber-800', dot: 'bg-amber-500' },
    approved: { label: 'Approved', ring: 'ring-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-800', dot: 'bg-emerald-500' },
    rejected: { label: 'Rejected', ring: 'ring-rose-200', bg: 'bg-rose-50', text: 'text-rose-800', dot: 'bg-rose-500' },
    draft: { label: 'Draft', ring: 'ring-slate-200', bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
};

const PAY_STATUS = {
    pending: { label: 'Pending', pill: 'bg-amber-100 text-amber-900 ring-amber-200/80' },
    approved: { label: 'Verified', pill: 'bg-emerald-100 text-emerald-900 ring-emerald-200/80' },
    rejected: { label: 'Rejected', pill: 'bg-rose-100 text-rose-900 ring-rose-200/80' },
};

const STAGE_CFG = {
    token: { label: 'Token', cls: 'bg-amber-100 text-amber-800 ring-amber-200/80' },
    loi: { label: 'LOI', cls: 'bg-sky-100 text-sky-800 ring-sky-200/80' },
    agreement: { label: 'Agreement', cls: 'bg-violet-100 text-violet-800 ring-violet-200/80' },
    paid: { label: 'Paid', cls: 'bg-emerald-100 text-emerald-800 ring-emerald-200/80' },
    refunded: { label: 'Refunded', cls: 'bg-rose-100 text-rose-800 ring-rose-200/80' },
};

const CLIENT_STATUS_CFG = {
    active: { label: 'Active', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200/80' },
    on_hold: { label: 'On hold', cls: 'bg-amber-50 text-amber-800 ring-amber-200/80' },
    cancelled: { label: 'Cancelled', cls: 'bg-rose-50 text-rose-700 ring-rose-200/80' },
    refunded: { label: 'Refunded', cls: 'bg-rose-100 text-rose-800 ring-rose-200/80' },
};

const LIFECYCLE_STAGES = ['token', 'loi', 'agreement', 'paid'];

function StatusPill({ status }) {
    const c = STATUS_CFG[status] || STATUS_CFG.draft;
    return (
        <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ring-1 ${c.ring} ${c.bg} ${c.text}`}>
            <span className={`h-2 w-2 rounded-full ${c.dot}`} />
            {c.label}
        </span>
    );
}

function StagePill({ stage }) {
    const c = STAGE_CFG[stage] || STAGE_CFG.token;
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ring-1 ${c.cls}`}>
            {c.label}
        </span>
    );
}

function ClientStatusPill({ status }) {
    const c = CLIENT_STATUS_CFG[status] || CLIENT_STATUS_CFG.active;
    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${c.cls}`}>
            {c.label}
        </span>
    );
}

function LifecycleTrack({ currentStage }) {
    const cur = currentStage || 'token';
    const idx = LIFECYCLE_STAGES.indexOf(cur);
    const activeIdx = idx >= 0 ? idx : 0;
    return (
        <div className="flex items-center gap-2 w-full">
            {LIFECYCLE_STAGES.map((st, i) => {
                const done = i < activeIdx;
                const isCurrent = i === activeIdx;
                return (
                    <div key={st} className="flex items-center gap-2 flex-1 min-w-0 last:flex-none">
                        <div className="flex flex-col items-center gap-1.5 min-w-0 flex-1">
                            <div className={`grid h-7 w-7 place-items-center rounded-full text-[10px] font-black transition ${
                                isCurrent ? 'bg-brand-teal text-white shadow-md shadow-brand-teal/25'
                                    : done ? 'bg-brand-teal-50 text-brand-teal-700 ring-1 ring-brand-teal/25'
                                        : 'bg-ink-100 text-ink-400'
                            }`}>
                                {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                            </div>
                            <span className={`text-[9px] font-bold uppercase tracking-wide truncate max-w-full text-center ${
                                isCurrent ? 'text-brand-teal-800' : done ? 'text-brand-teal-600' : 'text-ink-400'
                            }`}>
                                {STAGE_CFG[st]?.label || st}
                            </span>
                        </div>
                        {i < LIFECYCLE_STAGES.length - 1 && (
                            <div className={`h-px flex-1 min-w-[12px] mb-5 ${i < activeIdx ? 'bg-brand-teal/40' : 'bg-ink-200'}`} />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function collectDocuments(submission, stepBlocks) {
    const labelByKey = {};
    (submission?.schema?.steps || []).forEach(st => {
        (st.fields || []).forEach(f => { if (f.key) labelByKey[f.key] = f.label || f.key; });
    });
    const items = [];
    const seen = new Set();

    const push = (key, url, label, source) => {
        const href = Array.isArray(url) ? url[0] : url;
        if (!href || seen.has(href)) return;
        seen.add(href);
        items.push({ key, label: label || labelByKey[key] || key, href, source });
    };

    Object.entries(submission?.files || {}).forEach(([key, url]) => {
        push(key, url, labelByKey[key], 'registration');
    });

    stepBlocks?.forEach(({ rows }) => {
        rows?.forEach(({ f, isFile, raw }) => {
            if (isFile && raw) push(f.key, raw, f.label, 'registration');
        });
    });

    (submission?.secondPayments || []).forEach((p, i) => {
        if (p.proof?.path) push(`pay-proof-${i}`, p.proof.path, `Instalment #${i + 1} proof`, 'payment');
        if (p.financeProof?.path) push(`fin-proof-${i}`, p.financeProof.path, `Instalment #${i + 1} finance proof`, 'finance');
    });

    return items;
}

function isImagePath(path) {
    return /\.(png|jpe?g|gif|webp|svg)($|\?)/i.test(String(path || ''));
}

function isPdfPath(path) {
    return /\.pdf($|\?)/i.test(String(path || '')) || String(path || '').toLowerCase().includes('.pdf');
}

function DocumentCard({ doc }) {
    const href = uploadHref(doc.href);
    const isImg = isImagePath(href);
    const isPdf = isPdfPath(href);

    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="group block rounded-xl border border-ink-200/80 bg-white overflow-hidden ring-1 ring-ink-100/75 hover:ring-brand-teal/30 hover:shadow-[0_8px_28px_rgba(15,118,110,0.12)] transition"
        >
            <div className="relative aspect-[4/3] bg-gradient-to-br from-ink-50 to-slate-100 flex items-center justify-center overflow-hidden">
                {isImg ? (
                    <img src={href} alt={doc.label} className="h-full w-full object-cover group-hover:scale-[1.02] transition duration-300" />
                ) : isPdf ? (
                    <div className="text-center p-4">
                        <FileText className="h-12 w-12 text-brand-teal-700 mx-auto opacity-80" />
                        <span className="mt-2 block text-[10px] font-bold uppercase tracking-wider text-ink-500">PDF</span>
                    </div>
                ) : (
                    <FileText className="h-12 w-12 text-brand-teal-700 opacity-70" />
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink-900/70 to-transparent p-3 pt-8 opacity-0 group-hover:opacity-100 transition">
                    <span className="text-[11px] font-semibold text-white inline-flex items-center gap-1">
                        Open <ExternalLink className="h-3 w-3" />
                    </span>
                </div>
            </div>
            <div className="p-3 border-t border-ink-100">
                <div className="text-xs font-bold text-ink-900 line-clamp-2">{doc.label}</div>
                <div className="text-[10px] text-ink-500 mt-0.5 capitalize">{doc.source.replace('_', ' ')}</div>
            </div>
        </a>
    );
}

function MetricCard({ label, value, sub, accent, icon: Icon }) {
    return (
        <div className={`rounded-xl border px-4 py-3.5 ${
            accent ? 'border-brand-teal/20 bg-brand-teal-50/40' : 'border-ink-200/80 bg-white'
        }`}>
            <div className="flex items-center gap-2">
                {Icon && <Icon className={`h-3.5 w-3.5 shrink-0 ${accent ? 'text-brand-teal-600' : 'text-ink-400'}`} />}
                <dt className="text-[10px] font-bold uppercase tracking-wider text-ink-500">{label}</dt>
            </div>
            <dd className={`mt-1.5 text-base font-extrabold tabular-nums truncate ${accent ? 'text-brand-teal-900' : 'text-ink-900'}`}>{value}</dd>
            {sub && <p className="text-[10px] text-ink-500 mt-0.5">{sub}</p>}
        </div>
    );
}

function resolvePickerName(f, v, salesTeam) {
    if (v == null || v === '') return null;
    const id = String(v);
    const roleKeys = ['bda', 'bde', 'bdm'];
    if (f.type !== 'user_picker' && !roleKeys.includes(f.key)) return null;

    if (roleKeys.includes(f.key)) {
        const u = salesTeam?.[f.key];
        if (u?.name) return u.name;
    }
    for (const k of roleKeys) {
        const u = salesTeam?.[k];
        if (u && String(u._id || u) === id && u.name) return u.name;
    }
    return null;
}

function formatFieldValue(f, v, salesTeam) {
    if (f.type === 'file') return null;
    if (v === true) return 'Yes';
    if (v === false) return 'No';
    if (v == null || v === '') return null;

    const pickerName = resolvePickerName(f, v, salesTeam);
    if (pickerName) return pickerName;

    if (f.type === 'number' && (f.key === 'dealAmount' || f.key === 'tokenReceivedAmount' || f.key === 'budgetINR')) return inr(v);
    if (f.type === 'user_picker') return '—';
    return String(v);
}

function FileLinks({ f, urls }) {
    if (!urls) return <span className="text-ink-400">—</span>;
    const list = Array.isArray(urls) ? urls : [urls];
    if (!list.length) return <span className="text-ink-400">—</span>;
    return (
        <div className="flex flex-wrap gap-2 justify-end">
            {list.map((url, i) => (
                <a key={i} href={uploadHref(url)} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand-teal/10 text-brand-teal-800 px-2.5 py-1.5 text-xs font-medium ring-1 ring-brand-teal/20 hover:bg-brand-teal/20 transition">
                    <FileText className="h-3.5 w-3.5 shrink-0" />
                    {list.length > 1 ? `${f.label} · ${i + 1}` : 'Open file'}
                    <ExternalLink className="h-3 w-3 opacity-60" />
                </a>
            ))}
        </div>
    );
}

function fieldRowVisible(f, data, files) {
    if (f.type === 'header') return true;
    if (f.isArchived) {
        const isFile = f.type === 'file';
        const v = isFile ? files?.[f.key] : data?.[f.key];
        if (isFile) {
            if (!v) return false;
            if (Array.isArray(v) && !v.length) return false;
        } else if (v == null || v === '') return false;
    }
    return true;
}

function FinLine({ label, value, hint, highlight }) {
    return (
        <div className={`flex items-start justify-between gap-4 py-3 border-b border-ink-100 last:border-0 ${highlight ? 'bg-brand-teal-50/30 -mx-5 px-5 rounded-lg border-0 mb-1' : ''}`}>
            <div className="min-w-0">
                <p className={`text-sm ${highlight ? 'font-semibold text-brand-teal-900' : 'text-ink-600'}`}>{label}</p>
                {hint && <p className="text-[11px] text-ink-400 mt-0.5">{hint}</p>}
            </div>
            <p className={`text-sm text-right tabular-nums whitespace-nowrap shrink-0 ${highlight ? 'font-extrabold text-brand-teal-900' : 'font-semibold text-ink-900'}`}>{value}</p>
        </div>
    );
}

function SectionShell({ icon: Icon, title, subtitle, accent, children, headerRight, id }) {
    return (
        <section id={id} className="rounded-2xl border border-ink-200/70 bg-white shadow-sm overflow-hidden">
            <div className={`flex flex-wrap items-start justify-between gap-3 px-5 py-4 border-b border-ink-100 ${
                accent ? 'bg-gradient-to-r from-brand-teal-50/80 to-white' : 'bg-ink-50/30'
            }`}>
                <div className="flex items-start gap-3 min-w-0">
                    {Icon && (
                        <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${
                            accent ? 'bg-brand-teal text-white shadow-sm' : 'bg-white ring-1 ring-ink-200 text-brand-teal-700'
                        }`}>
                            <Icon className="h-4 w-4" />
                        </span>
                    )}
                    <div className="min-w-0">
                        <h2 className="text-sm font-extrabold text-ink-900 tracking-tight">{title}</h2>
                        {subtitle && <p className="text-xs mt-0.5 text-ink-500 leading-relaxed">{subtitle}</p>}
                    </div>
                </div>
                {headerRight}
            </div>
            <div>{children}</div>
        </section>
    );
}

function CollectionProgress({ pct, collected, balance, finalPayable }) {
    const clamped = Math.min(100, Math.max(0, pct));
    return (
        <div className="rounded-2xl border border-brand-teal/20 bg-white p-5 shadow-sm">
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Collection progress</p>
            <div className="mt-4 flex items-center gap-4">
                <div className="relative h-16 w-16 shrink-0">
                    <svg className="h-16 w-16 -rotate-90" viewBox="0 0 64 64" aria-hidden>
                        <circle cx="32" cy="32" r="26" fill="none" stroke="currentColor" strokeWidth="6" className="text-ink-100" />
                        <circle
                            cx="32" cy="32" r="26" fill="none" stroke="currentColor" strokeWidth="6"
                            strokeLinecap="round"
                            strokeDasharray={`${2 * Math.PI * 26}`}
                            strokeDashoffset={`${2 * Math.PI * 26 * (1 - clamped / 100)}`}
                            className="text-brand-teal"
                        />
                    </svg>
                    <span className="absolute inset-0 grid place-items-center text-sm font-black text-brand-teal-800 tabular-nums">
                        {clamped.toFixed(0)}%
                    </span>
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                    <p className="text-xs text-ink-500">Collected <span className="font-bold text-ink-800">{inr(collected)}</span></p>
                    <p className="text-xs text-ink-500">Balance <span className="font-bold text-rose-700">{inr(balance)}</span></p>
                    {finalPayable > 0 && (
                        <p className="text-[10px] text-ink-400">of {inr(finalPayable)} payable</p>
                    )}
                </div>
            </div>
            <div className="mt-3 h-2 rounded-full bg-ink-100 overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-brand-teal to-teal-400 transition-all" style={{ width: `${clamped}%` }} />
            </div>
        </div>
    );
}

function SidebarInfoCard({ title, icon: Icon, children }) {
    return (
        <div className="rounded-2xl border border-ink-200/70 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
                {Icon && <Icon className="h-4 w-4 text-brand-teal-600" />}
                <h3 className="text-xs font-bold uppercase tracking-wider text-ink-500">{title}</h3>
            </div>
            {children}
        </div>
    );
}

function InfoRow({ icon: Icon, label, value }) {
    if (!value || value === '—') return null;
    return (
        <div className="flex items-start gap-2.5 py-2 border-b border-ink-50 last:border-0">
            {Icon && <Icon className="h-4 w-4 text-ink-400 shrink-0 mt-0.5" />}
            <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-400">{label}</p>
                <p className="text-sm font-medium text-ink-900 break-words">{value}</p>
            </div>
        </div>
    );
}

function RegistrationField({ f, isFile, raw, text }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-[minmax(140px,32%)_1fr] gap-2 sm:gap-4 py-3.5 border-b border-ink-50 last:border-0">
            <div>
                <p className="text-xs font-semibold text-ink-600">{f.label}</p>
                {f.isArchived && (
                    <span className="mt-1 inline-block text-[9px] font-bold uppercase text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded ring-1 ring-amber-200/60">Archived</span>
                )}
            </div>
            <div className="text-sm text-ink-900 sm:text-right">
                {isFile ? <FileLinks f={f} urls={raw} /> : (text != null ? <span className="font-medium">{text}</span> : <span className="text-ink-300">—</span>)}
            </div>
        </div>
    );
}

/**
 * Full-page submission detail — /admin/clients/:id and /sales/clients/:id.
 * Second payments (Daewoo-style): sales adds → admin verifies with finance proof.
 */
export default function ClientSubmissionDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const user = useAuth(st => st.user);
    const roleDoc = useRoleDoc(user);
    const finance = useSettings(st => st.finance);
    const paymentSettings = useSettings(st => st.paymentSettings);
    const legal = useSettings(st => st.legal);
    const [s, setS] = useState(null);
    const [loading, setLoading] = useState(true);
    const [payBusy, setPayBusy] = useState(false);
    const [openPayModal, setOpenPayModal] = useState(false);
    const [openEditClient, setOpenEditClient] = useState(false);
    const [approveBusy, setApproveBusy] = useState(false);
    const [addAmount, setAddAmount] = useState('');
    const [addDate, setAddDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [addMode, setAddMode] = useState('Bank Transfer');
    const [addTxn, setAddTxn] = useState('');
    const [addProof, setAddProof] = useState(null);
    const [approvePid, setApprovePid] = useState(null);
    const [finProof, setFinProof] = useState(null);
    const [finDesc, setFinDesc] = useState('');
    const [openReqEdit, setOpenReqEdit] = useState(false);
    const [openReqDelete, setOpenReqDelete] = useState(false);
    const [openAdminDelete, setOpenAdminDelete] = useState(false);
    const [deleteReason, setDeleteReason] = useState('');
    const [adminDeleteReason, setAdminDeleteReason] = useState('');
    const [reqBusy, setReqBusy] = useState(false);
    const [adminDeleteBusy, setAdminDeleteBusy] = useState(false);
    const [reviewBusyId, setReviewBusyId] = useState(null);

    const location = useLocation();
    const isAdminRoute = location.pathname.startsWith('/admin/');
    const backTo = isAdminRoute ? '/admin/clients' : '/sales/clients';
    const canReview = ['admin', 'manager'].includes(user?.role) && s?.status === 'submitted';

    useEffect(() => {
        let cancel = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/forms/submissions/${id}`);
                if (!cancel) setS(data);
            } catch {
                if (!cancel) toast.error('Could not load submission');
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, [id]);

    const stepBlocks = useMemo(() => {
        if (!s?.schema?.steps) return [];
        return (s.schema.steps || []).map((step, idx) => {
            const fields = (step.fields || []).filter(f => fieldRowVisible(f, s.data, s.files));
            const rows = fields.filter(f => f.type !== 'header').map(f => {
                const isFile = f.type === 'file';
                const raw = isFile ? s.files?.[f.key] : s.data?.[f.key];
                const text = !isFile ? formatFieldValue(f, raw, s.salesTeam) : null;
                return { f, isFile, raw, text };
            });
            const headers = fields.filter(f => f.type === 'header');
            if (!rows.length && !headers.length) return null;
            return { step, idx, rows, headers };
        }).filter(Boolean);
    }, [s]);

    const documents = useMemo(() => collectDocuments(s, stepBlocks), [s, stepBlocks]);

    const salesTeamLine = useMemo(() => {
        const team = s?.salesTeam;
        if (!team) return [];
        return [
            team.bda && { role: 'BDA', user: team.bda },
            team.bde && { role: 'BDE', user: team.bde },
            team.bdm && { role: 'BDM', user: team.bdm },
        ].filter(Boolean);
    }, [s]);

    const secondApprovedSum = useMemo(() => {
        if (!s?.secondPayments?.length) return 0;
        /* Exclude refunded and token-promoted instalments — they no longer
           count toward the second-payment total (mirrors the server hook). */
        return s.secondPayments
            .filter(p => p.status === 'approved' && !p.isRefunded && !p.promotedToToken)
            .reduce((a, p) => a + Number(p.amount || 0), 0);
    }, [s]);

    const financePreview = useMemo(
        () => (s?.data ? submissionFinancePreview(s.data, finance, paymentSettings, { secondPaymentsApprovedSum: secondApprovedSum }) : null),
        [s, finance, paymentSettings, secondApprovedSum],
    );

    const review = async (status) => {
        let reviewNote = '';
        if (status === 'rejected') reviewNote = prompt('Reason for rejection (optional):') || '';
        try {
            await api.post(`/forms/submissions/${s._id}/review`, { status, reviewNote });
            toast.success(`Marked ${status}`);
            const { data } = await api.get(`/forms/submissions/${id}`);
            setS(data);
        } catch { /* toasted */ }
    };

    const uid = user?._id || user?.id;
    const ownerId = s?.submittedBy?._id || s?.submittedBy;
    const isOwner = uid && ownerId && String(ownerId) === String(uid);
    const isOnSalesTeam = (() => {
        const me = String(uid || '');
        return me && [s?.salesTeam?.bda, s?.salesTeam?.bde, s?.salesTeam?.bdm]
            .map(x => String(x?._id || x || ''))
            .includes(me);
    })();
    const isLinked = isOwner || isOnSalesTeam;

    const canAddSecondPayment = s?.status === 'approved' && (
        ['admin', 'manager', 'finance'].includes(user?.role) ||
        (user?.role === 'sales_user' && isLinked)
    );

    const canUpdateLoi = s?.status === 'approved' && canUpdateLoiStage(user, roleDoc);
    const canUpdateAgreement = s?.status === 'approved' && canUpdateAgreementStage(user, roleDoc);

    const canVerifyPayments = isAdminRoute && ['admin', 'manager', 'finance'].includes(user?.role);
    const canRequestClientChange = canRequestClientChangeOnSubmission(user, s);
    const canReviewChangeRequests = ['admin', 'manager'].includes(user?.role);
    const canAdminDeleteClient = isAdminRoute && user?.role === ADMIN_ROLE;
    const changeRequests = s?.changeRequests || [];
    const myPendingRequest = changeRequests.find(r =>
        r.status === 'pending'
        && String(r.requestedBy?._id || r.requestedBy) === String(uid)
    );

    const deleteClientAsAdmin = async (e) => {
        e.preventDefault();
        if (!adminDeleteReason.trim()) {
            toast.error('Please add a reason for delete');
            return;
        }
        const name = s?.data?.name || 'this client';
        if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;

        setAdminDeleteBusy(true);
        try {
            await api.delete(`/forms/submissions/${s._id}`, { data: { reason: adminDeleteReason.trim() } });
            toast.success('Client deleted');
            nav(backTo);
        } catch { /* interceptor */ }
        finally { setAdminDeleteBusy(false); }
    };

    const submitDeleteRequest = async (e) => {
        e.preventDefault();
        if (!deleteReason.trim()) {
            toast.error('Please add a reason for delete');
            return;
        }
        setReqBusy(true);
        try {
            const { data } = await api.post(`/forms/submissions/${s._id}/change-requests/delete`, {
                reason: deleteReason,
            });
            setS(data.submission);
            setOpenReqDelete(false);
            setDeleteReason('');
            toast.success('Delete request sent for admin approval');
        } catch { /* interceptor */ }
        finally { setReqBusy(false); }
    };

    const reviewChangeRequest = async (requestId, decision) => {
        const reviewNote = decision === 'reject' ? (prompt('Reason for rejection (optional):') || '') : '';
        setReviewBusyId(requestId);
        try {
            const { data } = await api.post(`/forms/submissions/${s._id}/change-requests/${requestId}/review`, {
                decision,
                reviewNote,
            });
            setS(data.submission);
            toast.success(decision === 'approve' ? 'Request approved — changes applied' : 'Request rejected');
        } catch { /* interceptor */ }
        finally { setReviewBusyId(null); }
    };

    /** Returns true on success so the caller can close the modal. */
    const submitSecondPayment = async (e) => {
        e.preventDefault();
        const amt = Number(addAmount);
        if (!amt || amt <= 0) {
            toast.error('Enter a valid amount');
            return false;
        }
        setPayBusy(true);
        try {
            const fd = new FormData();
            fd.append('amount', String(amt));
            fd.append('paymentDate', addDate);
            fd.append('mode', addMode);
            fd.append('transactionId', addTxn);
            if (addProof) fd.append('proof', addProof);
            const { data } = await api.post(`/forms/submissions/${s._id}/payments`, fd);
            setS(data);
            toast.success('Payment recorded — pending admin verification');
            setAddAmount('');
            setAddTxn('');
            setAddProof(null);
            return true;
        } catch { /* interceptor */ return false; }
        finally { setPayBusy(false); }
    };

    const submitApprove = async (e) => {
        e.preventDefault();
        if (!finProof) {
            toast.error('Attach finance verification proof');
            return;
        }
        setApproveBusy(true);
        try {
            const fd = new FormData();
            fd.append('financeProofDescription', finDesc);
            fd.append('financeProof', finProof);
            const { data } = await api.post(`/forms/submissions/${s._id}/payments/${approvePid}/approve`, fd);
            setS(data);
            toast.success('Payment verified');
            setApprovePid(null);
            setFinProof(null);
            setFinDesc('');
        } catch { /* interceptor */ }
        finally { setApproveBusy(false); }
    };

    const rejectPayment = async (paymentId) => {
        const reviewNote = prompt('Reason for rejection (optional):') || '';
        try {
            const { data } = await api.post(`/forms/submissions/${s._id}/payments/${paymentId}/reject`, { reviewNote });
            setS(data);
            toast.success('Payment rejected');
        } catch { /* interceptor */ }
    };

    if (loading) {
        return (
            <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 text-ink-500">
                <Loader2 className="h-8 w-8 animate-spin text-brand-teal" />
                <p className="text-sm font-medium">Loading client details…</p>
            </div>
        );
    }
    if (!s) {
        return (
            <div className="max-w-lg mx-auto text-center py-16">
                <p className="text-ink-600 mb-4">Submission not found or you do not have access.</p>
                <Link to={backTo} className="ag-btn-primary inline-flex">Back to list</Link>
            </div>
        );
    }

    const city = s.data?.city || s.data?.personalCity;
    const deal = s.data?.dealAmount ?? s.data?.budgetINR;
    const snap = financePreview?.snapshot;
    const loiBaseLabel = paymentSettings.loiCalculationType === 'final_payable' ? 'on final payable' : 'on deal amount';

    const showFinance = financePreview && (financePreview.dealAmount > 0 || financePreview.tokenReceived > 0 || secondApprovedSum > 0);
    const payments = s.secondPayments || [];
    const stage = s.currentStage || 'token';
    const clientStatus = s.clientStatus || 'active';
    const collected = Number(s.totalReceived || financePreview?.totalReceivedTowardPayable || 0);
    const balance = Number(s.balanceAmount ?? financePreview?.balanceAfterToken ?? 0);
    /* Single source of truth for the collection % and current token — server
       rollups (refund- and promotion-aware). Everything on this page reads
       these so the number never disagrees with itself. */
    const receivedPct = Number(s.receivedPercent ?? financePreview?.receivedPercent ?? 0);
    const effToken = Number(s.effectiveTokenAmount ?? financePreview?.tokenReceived ?? 0);

    const clientInitial = (s.data?.name || 'C').charAt(0).toUpperCase();

    return (
        <div className="min-h-screen bg-gradient-to-b from-ink-50/60 via-white to-white pb-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 space-y-6">
                {/* Top bar */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => nav(backTo)}
                        className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-ink-600 hover:text-brand-teal-800 hover:bg-white transition"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        {isAdminRoute ? 'Client ledger' : 'My clients'}
                    </button>
                    <div className="flex flex-wrap items-center gap-2">
                        {myPendingRequest && (
                            <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-[11px] font-semibold text-amber-900 ring-1 ring-amber-200">
                                Pending {myPendingRequest.type}
                            </span>
                        )}
                        {canRequestClientChange && (
                            <>
                                <button type="button" onClick={() => setOpenReqEdit(true)} className="ag-btn-outline text-sm py-2">
                                    <Edit3 className="h-4 w-4" /> Request edit
                                </button>
                                <button type="button" onClick={() => setOpenReqDelete(true)} className="ag-btn-ghost text-sm py-2 text-rose-700 ring-1 ring-rose-200">
                                    <Trash2 className="h-4 w-4" /> Request delete
                                </button>
                            </>
                        )}
                        {isAdminRoute && ['admin', 'manager'].includes(user?.role) && (
                            <button type="button" onClick={() => setOpenEditClient(true)} className="ag-btn-primary text-sm py-2">
                                <Edit3 className="h-4 w-4" /> Edit client
                            </button>
                        )}
                        {canAdminDeleteClient && (
                            <button type="button" onClick={() => setOpenAdminDelete(true)} className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 transition">
                                <Trash2 className="h-4 w-4" /> Delete
                            </button>
                        )}
                    </div>
                </div>

                {/* Hero */}
                <div className="rounded-2xl border border-ink-200/70 bg-white shadow-sm overflow-hidden">
                    <div className="h-1.5 bg-gradient-to-r from-brand-teal via-teal-500 to-brand-teal-700" />
                    <div className="p-6 sm:p-8">
                        <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                            <div className="flex items-start gap-4 min-w-0 flex-1">
                                <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand-teal to-brand-teal-700 text-white text-xl font-black shadow-md shadow-brand-teal/20">
                                    {clientInitial}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-brand-teal-600 font-mono">#{short(s._id)}</span>
                                        <span className="text-ink-300">·</span>
                                        <span className="text-[10px] font-semibold text-ink-500">{s.schema?.name || 'Client registration'}</span>
                                    </div>
                                    <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-ink-900 tracking-tight">
                                        {s.data?.name || 'Unnamed lead'}
                                    </h1>
                                    <p className="mt-2 text-sm text-ink-500 flex flex-wrap gap-x-4 gap-y-1">
                                        <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5" />{s.submittedBy?.name || '—'}</span>
                                        <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{fd(s.createdAt)}</span>
                                    </p>
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        <StatusPill status={s.status} />
                                        <StagePill stage={stage} />
                                        <ClientStatusPill status={clientStatus} />
                                    </div>
                                </div>
                            </div>
                            {showFinance && snap && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 lg:max-w-md shrink-0 w-full">
                                    <MetricCard label="Deal" value={deal != null && deal !== '' ? inr(deal) : '—'} icon={Banknote} />
                                    <MetricCard label="Collected" value={inr(collected)} accent icon={Wallet} />
                                    <MetricCard label="Balance" value={inr(balance)} accent={balance > 0} sub={`${receivedPct.toFixed(0)}% received`} />
                                </div>
                            )}
                        </div>
                        {/* <div className="mt-6 pt-6 border-t border-ink-100">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-400 mb-3">Deal lifecycle</p>
                            <LifecycleTrack currentStage={stage} />
                        </div> */}
                    </div>
                </div>

                {/* Two-column body */}
                <div className="grid lg:grid-cols-12 gap-6 items-start">
                    <div className="lg:col-span-8 space-y-6 min-w-0">

            {s.status === 'approved' && (legal?.loiStages?.length > 0) && (
                <LoiProgressSection
                    variant="loi"
                    submission={s}
                    loiStages={legal.loiStages}
                    canUpdate={canUpdateLoi}
                    onUpdated={setS}
                />
            )}

            {s.status === 'approved' && (legal?.agreementStages?.length > 0) && (
                <LoiProgressSection
                    variant="agreement"
                    submission={s}
                    stages={legal.agreementStages}
                    canUpdate={canUpdateAgreement}
                    onUpdated={setS}
                />
            )}

            {showFinance && snap && (
                <SectionShell
                    accent
                    id="finance"
                    icon={Banknote}
                    title="Contract & tax"
                    subtitle="GST, TDS, final payable, and collection thresholds."
                    headerRight={(
                        <span className="inline-flex items-center rounded-full bg-brand-teal/10 px-3 py-1 text-xs font-bold text-brand-teal-800 ring-1 ring-brand-teal/20">
                            {receivedPct.toFixed(1)}% collected
                        </span>
                    )}
                >
                    <div className="p-5 grid sm:grid-cols-3 gap-3 border-b border-ink-100 bg-ink-50/20">
                        <div className="rounded-xl bg-white border border-ink-200/80 p-4 text-center">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-400">Final payable</p>
                            <p className="mt-1 text-lg font-black text-ink-900 tabular-nums">{inr(snap.finalPayableAmount)}</p>
                        </div>
                        <div className="rounded-xl bg-brand-teal-50 border border-brand-teal/20 p-4 text-center">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-brand-teal-600">Collected</p>
                            <p className="mt-1 text-lg font-black text-brand-teal-900 tabular-nums">{inr(collected)}</p>
                        </div>
                        <div className="rounded-xl bg-white border border-rose-200/60 p-4 text-center">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-rose-500">Balance due</p>
                            <p className="mt-1 text-lg font-black text-rose-700 tabular-nums">{inr2(balance)}</p>
                        </div>
                    </div>
                    <div className="px-5 py-4">
                        <FinLine label="Deal type" value={<span className="capitalize">{snap.dealType}</span>} />
                        <FinLine label="Deal amount" value={inr(financePreview.dealAmount)} />
                        <FinLine label="GST" value={inr(snap.gstAmount)} hint={`Base ${inr(snap.gstBase)} @ ${snap.gstRate}%`} />
                        <FinLine label="TDS" value={inr(snap.tdsAmount)} hint={`Base ${inr(snap.tdsBase)} @ ${snap.tdsRate}%`} />
                        <FinLine label="Token + verified instalments" value={inr(collected)} hint={`Token ${inr(effToken)} · 2nd+ ${inr(secondApprovedSum)}`} />
                        <FinLine label={`LOI threshold (${loiBaseLabel})`} value={inr2(financePreview.loiThreshold)} />
                        <FinLine label="Agreement threshold" value={inr2(financePreview.agreementThreshold)} highlight />
                    </div>
                    {isAdminRoute && ['admin', 'manager'].includes(user?.role) && (
                        <TdsRateEditor
                            submission={s}
                            snap={snap}
                            defaultTdsPercent={finance?.defaultTdsPercent ?? 2}
                            onSaved={setS}
                        />
                    )}
                    {snap.challanRequired && (
                        <div className="mx-5 mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                            TDS challan may be required — TDS amount is greater than zero.
                        </div>
                    )}
                </SectionShell>
            )}

            {(payments.length > 0 || canAddSecondPayment) && (
                <SectionShell
                    id="payments"
                    icon={Wallet}
                    title="Payments"
                    subtitle="Instalments submitted by sales and verified by finance."
                >
                    {payments.length > 0 && (
                        <div className="overflow-x-auto border-b border-ink-100">
                            <table className="w-full min-w-[720px] text-sm">
                                <thead>
                                    <tr className="bg-ink-50 text-[10px] font-bold uppercase tracking-wider text-ink-500 border-b border-ink-200">
                                        <th className="text-left px-4 py-3">#</th>
                                        <th className="text-left px-4 py-3">Amount</th>
                                        <th className="text-left px-4 py-3">Date</th>
                                        <th className="text-left px-4 py-3">Mode</th>
                                        <th className="text-left px-4 py-3">Txn ID</th>
                                        <th className="text-left px-4 py-3">Sales proof</th>
                                        <th className="text-left px-4 py-3">Status</th>
                                        <th className="text-left px-4 py-3">By</th>
                                        {canVerifyPayments && <th className="text-right px-4 py-3">Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {payments.map((p, i) => {
                                        const st = PAY_STATUS[p.status] || PAY_STATUS.pending;
                                        return (
                                            <tr key={p._id || i} className="border-b border-ink-100 hover:bg-brand-teal/[0.03]">
                                                <td className="px-4 py-3 text-ink-500 font-mono text-xs">{i + 1}</td>
                                                <td className="px-4 py-3 font-bold tabular-nums">{inr(p.amount)}</td>
                                                <td className="px-4 py-3 text-ink-700 whitespace-nowrap">{p.paymentDate ? new Date(p.paymentDate).toLocaleDateString('en-IN') : '—'}</td>
                                                <td className="px-4 py-3 text-ink-700">{p.mode || '—'}</td>
                                                <td className="px-4 py-3 font-mono text-xs text-ink-600 max-w-[120px] truncate" title={p.transactionId}>{p.transactionId || '—'}</td>
                                                <td className="px-4 py-3">
                                                    {p.proof?.path ? (
                                                        <a href={uploadHref(p.proof.path)} target="_blank" rel="noreferrer" className="text-brand-teal-700 font-medium hover:underline inline-flex items-center gap-1">
                                                            <FileText className="h-3.5 w-3.5" /> View
                                                        </a>
                                                    ) : <span className="text-ink-400">—</span>}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ${st.pill}`}>
                                                        {p.status === 'pending' && <Clock className="h-3 w-3" />}
                                                        {p.status === 'approved' && <CheckCircle2 className="h-3 w-3" />}
                                                        {p.status === 'rejected' && <XCircle className="h-3 w-3" />}
                                                        {st.label}
                                                    </span>
                                                    {p.status === 'approved' && p.financeProof?.path && (
                                                        <div className="mt-1">
                                                            <a href={uploadHref(p.financeProof.path)} className="text-[11px] text-brand-teal-700 hover:underline" target="_blank" rel="noreferrer">Finance proof</a>
                                                        </div>
                                                    )}
                                                    {p.promotedToToken && (
                                                        <div className="mt-1">
                                                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200">
                                                                ⤴ Promoted to token
                                                            </span>
                                                        </div>
                                                    )}
                                                    {p.isRefunded && (
                                                        <div className="mt-1">
                                                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-rose-50 text-rose-700 ring-1 ring-rose-200">
                                                                Refunded {p.refundedAmount ? inr(p.refundedAmount) : ''}
                                                            </span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-xs text-ink-600">{p.addedBy?.name || '—'}</td>
                                                {canVerifyPayments && (
                                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                                        {p.status === 'pending' && (
                                                            <div className="flex flex-wrap justify-end gap-2">
                                                                <button type="button" className="ag-btn-primary text-xs py-1.5 px-3" onClick={() => { setApprovePid(p._id); setFinProof(null); setFinDesc(''); }}>
                                                                    Verify
                                                                </button>
                                                                <button type="button" className="ag-btn-ghost text-xs py-1.5 px-3 text-rose-700 ring-1 ring-rose-200" onClick={() => rejectPayment(p._id)}>
                                                                    Reject
                                                                </button>
                                                            </div>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {canAddSecondPayment && (
                        <div className="p-5 flex items-center justify-between gap-3 bg-ink-50/40 border-t border-ink-100">
                            <div className="text-xs text-ink-600">
                                Need to log an instalment? Recorded payments stay <b>pending</b> until admin verifies them with a finance proof.
                            </div>
                            <button type="button" className="ag-btn-primary" onClick={() => setOpenPayModal(true)}>
                                <Banknote className="h-4 w-4" /> Record new payment
                            </button>
                        </div>
                    )}

                    {/* Add-payment modal (popup, like Daewoo) */}
                    {openPayModal && canAddSecondPayment && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-900/50 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="add-pay-title">
                            <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl ring-1 ring-ink-200">
                                <div className="px-5 py-4 border-b border-ink-100 flex items-center justify-between">
                                    <div>
                                        <h3 id="add-pay-title" className="font-extrabold">Record payment</h3>
                                        <p className="text-[11px] text-ink-500 mt-0.5">
                                            Goes to admin for finance verification. You can attach a proof now or later.
                                        </p>
                                    </div>
                                    <button type="button" className="text-ink-500 hover:text-brand-red" onClick={() => setOpenPayModal(false)} aria-label="Close">
                                        <XIcon className="h-4 w-4" />
                                    </button>
                                </div>
                                <form onSubmit={async (e) => {
                                    const ok = await submitSecondPayment(e);
                                    if (ok) setOpenPayModal(false);
                                }} className="p-5 space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <label className="block">
                                            <span className="ag-label">Amount (INR) *</span>
                                            <input className="ag-input" type="number" min="1" step="1" autoFocus
                                                value={addAmount} onChange={e => setAddAmount(e.target.value)} required />
                                        </label>
                                        <label className="block">
                                            <span className="ag-label">Payment date *</span>
                                            <input className="ag-input" type="date" value={addDate} onChange={e => setAddDate(e.target.value)} required />
                                        </label>
                                        <label className="block">
                                            <span className="ag-label">Mode</span>
                                            <select className="ag-input" value={addMode} onChange={e => setAddMode(e.target.value)}>
                                                {['Bank Transfer', 'UPI', 'Cheque', 'Cash', 'Other'].map(m => <option key={m} value={m}>{m}</option>)}
                                            </select>
                                        </label>
                                        <label className="block">
                                            <span className="ag-label">Transaction / UTR ID</span>
                                            <input className="ag-input" value={addTxn} onChange={e => setAddTxn(e.target.value)} placeholder="Optional" />
                                        </label>
                                    </div>
                                    <label className="block">
                                        <span className="ag-label">Payment proof (optional)</span>
                                        <input className="ag-input text-sm file:mr-3 file:rounded-md file:border-0 file:bg-brand-teal-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-brand-teal-800"
                                            type="file" accept="image/*,.pdf" onChange={e => setAddProof(e.target.files?.[0] || null)} />
                                    </label>
                                    <div className="flex justify-end gap-2 pt-2">
                                        <button type="button" className="ag-btn-ghost" onClick={() => setOpenPayModal(false)}>Cancel</button>
                                        <button type="submit" className="ag-btn-primary" disabled={payBusy}>
                                            {payBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                            {payBusy ? 'Submitting…' : 'Submit for verification'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </SectionShell>
            )}

            {(changeRequests.length > 0 && canReviewChangeRequests) && (
                <SectionShell
                    icon={Edit3}
                    title="Pending change requests"
                    subtitle="Review sales requests to update or delete this client."
                >
                    <div className="p-5 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-wider text-ink-500">
                                    <th className="text-left py-1">When</th>
                                    <th className="text-left py-1">Type</th>
                                    <th className="text-left py-1">By</th>
                                    <th className="text-left py-1">Reason</th>
                                    <th className="text-left py-1">Fields</th>
                                    <th className="text-left py-1">Status</th>
                                    <th className="text-right py-1">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {changeRequests.slice().reverse().map(r => (
                                    <tr key={r._id} className="border-t border-ink-100">
                                        <td className="py-2 text-xs text-ink-500 whitespace-nowrap">{new Date(r.requestedAt).toLocaleString('en-IN')}</td>
                                        <td className="py-2 text-xs font-semibold uppercase">{r.type}</td>
                                        <td className="py-2 text-xs">{r.requestedBy?.name || '—'}</td>
                                        <td className="py-2 text-xs text-ink-600 max-w-[200px] truncate" title={r.reason}>{r.reason || '—'}</td>
                                        <td className="py-2 text-xs text-ink-500">
                                            {r.type === 'update' ? Object.keys(r.patch || {}).join(', ') || '—' : '—'}
                                        </td>
                                        <td className="py-2">
                                            <span className={`ag-chip ${r.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : r.status === 'rejected' ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'}`}>
                                                {r.status}
                                            </span>
                                        </td>
                                        <td className="py-2 text-right whitespace-nowrap">
                                            {r.status === 'pending' ? (
                                                <div className="flex justify-end gap-2">
                                                    <button type="button" className="ag-btn-primary text-xs py-1.5 px-3" disabled={reviewBusyId === r._id} onClick={() => reviewChangeRequest(r._id, 'approve')}>
                                                        Approve
                                                    </button>
                                                    <button type="button" className="ag-btn-ghost text-xs py-1.5 px-3 text-rose-700 ring-1 ring-rose-200" disabled={reviewBusyId === r._id} onClick={() => reviewChangeRequest(r._id, 'reject')}>
                                                        Reject
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-[11px] text-ink-500">{r.reviewedBy?.name ? `by ${r.reviewedBy.name}` : '—'}</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </SectionShell>
            )}

            {approvePid && canVerifyPayments && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-900/50 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="verify-pay-title">
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-xl ring-1 ring-ink-200 p-6 space-y-4">
                        <h3 id="verify-pay-title" className="text-lg font-extrabold text-ink-900">Verify payment</h3>
                        <p className="text-sm text-ink-600">Upload the finance verification document (required), same as Daewoo admin approval.</p>
                        <form onSubmit={submitApprove} className="space-y-3">
                            <label className="block">
                                <span className="ag-label">Finance proof file</span>
                                <input className="ag-input" type="file" accept="image/*,.pdf" required onChange={e => setFinProof(e.target.files?.[0] || null)} />
                            </label>
                            <label className="block">
                                <span className="ag-label">Description (optional)</span>
                                <textarea className="ag-input min-h-[80px]" value={finDesc} onChange={e => setFinDesc(e.target.value)} />
                            </label>
                            <div className="flex gap-2 justify-end pt-2">
                                <button type="button" className="ag-btn-ghost" onClick={() => { setApprovePid(null); setFinProof(null); }}>Cancel</button>
                                <button type="submit" className="ag-btn-primary" disabled={approveBusy}>
                                    {approveBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                    {approveBusy ? 'Saving…' : 'Approve'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {documents.length > 0 && (
                <SectionShell
                    id="documents"
                    icon={FileStack}
                    title="Documents & proofs"
                    subtitle={`${documents.length} file${documents.length === 1 ? '' : 's'} attached`}
                >
                    <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {documents.map(doc => (
                            <DocumentCard key={`${doc.key}-${doc.href}`} doc={doc} />
                        ))}
                    </div>
                </SectionShell>
            )}

            <SectionShell id="registration" icon={ClipboardList} title="Registration data" subtitle="All fields submitted during client onboarding.">
                <div className="divide-y divide-ink-100">
                    {stepBlocks.length === 0 && (
                        <p className="px-5 py-8 text-sm text-ink-500 text-center">No registration fields to display.</p>
                    )}
                    {stepBlocks.map(({ step, idx, rows, headers }) => {
                        const displayRows = documents.length > 0 ? rows.filter(r => !r.isFile) : rows;
                        if (!displayRows.length && !headers.length) return null;
                        return (
                            <div key={idx}>
                                <div className="px-5 py-3 bg-brand-teal-50/40 border-b border-brand-teal/10 flex items-center gap-2">
                                    <span className="grid h-6 w-6 place-items-center rounded-md bg-brand-teal text-white text-[10px] font-black">{idx + 1}</span>
                                    <div>
                                        <h3 className="text-sm font-bold text-ink-900">{step.title}</h3>
                                        {step.description && <p className="text-[11px] text-ink-500">{step.description}</p>}
                                    </div>
                                </div>
                                {headers.map(h => (
                                    <div key={h.key} className="px-5 py-2 bg-ink-50/50 border-b border-ink-100">
                                        <h4 className="text-xs font-semibold text-ink-700">{h.label}</h4>
                                    </div>
                                ))}
                                <div className="px-5">
                                    {displayRows.map(({ f, isFile, raw, text }) => (
                                        <RegistrationField key={f.key} f={f} isFile={isFile} raw={raw} text={text} />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </SectionShell>

            <RefundsBlock s={s} user={user} isOwner={isLinked} onChange={async () => {
                try { const { data } = await api.get(`/forms/submissions/${id}`); setS(data); }
                catch {/* ignore */ }
            }} />

            {s.reviewNote && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-rose-900">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-rose-600">Review note</p>
                    <p className="text-sm mt-1">{s.reviewNote}</p>
                </div>
            )}

            {canReview && (
                <div className="rounded-2xl border border-ink-200 bg-white p-5 shadow-sm flex flex-col sm:flex-row gap-3">
                    <button type="button" className="ag-btn-primary flex-1 justify-center py-3" onClick={() => review('approved')}>
                        <Check className="h-5 w-5" /> Approve submission
                    </button>
                    <button type="button" className="ag-btn-danger flex-1 justify-center py-3" onClick={() => review('rejected')}>
                        <XIcon className="h-5 w-5" /> Reject submission
                    </button>
                </div>
            )}

                    </div>{/* end main column */}

                    <aside className="lg:col-span-4 space-y-5 lg:sticky lg:top-4">
                        {showFinance && snap && (
                            <CollectionProgress
                                pct={receivedPct}
                                collected={collected}
                                balance={balance}
                                finalPayable={snap.finalPayableAmount}
                            />
                        )}
                        <SidebarInfoCard title="Contact" icon={User}>
                            <InfoRow icon={Phone} label="Phone" value={s.data?.phone} />
                            <InfoRow icon={Mail} label="Email" value={s.data?.email} />
                            <InfoRow icon={MapPin} label="Location" value={[city, s.data?.state].filter(Boolean).join(', ') || null} />
                            <InfoRow icon={Building2} label="Office branch" value={s.data?.officeBranch} />
                        </SidebarInfoCard>
                        {salesTeamLine.length > 0 && (
                            <SidebarInfoCard title="Sales team" icon={Users}>
                                {salesTeamLine.map(({ role, user: u }) => (
                                    <div key={role} className="flex items-center justify-between py-2 border-b border-ink-50 last:border-0">
                                        <span className="text-[10px] font-bold uppercase text-brand-teal-600">{role}</span>
                                        <span className="text-sm font-medium text-ink-900">{u?.name || '—'}</span>
                                    </div>
                                ))}
                            </SidebarInfoCard>
                        )}
                        <SidebarInfoCard title="Quick links" icon={ChevronRight}>
                            <nav className="space-y-1">
                                {showFinance && <a href="#finance" className="block text-sm font-medium text-brand-teal-700 hover:text-brand-teal-900 py-1.5">Contract & tax</a>}
                                {(payments.length > 0 || canAddSecondPayment) && <a href="#payments" className="block text-sm font-medium text-brand-teal-700 hover:text-brand-teal-900 py-1.5">Payments</a>}
                                {documents.length > 0 && <a href="#documents" className="block text-sm font-medium text-brand-teal-700 hover:text-brand-teal-900 py-1.5">Documents ({documents.length})</a>}
                                <a href="#registration" className="block text-sm font-medium text-brand-teal-700 hover:text-brand-teal-900 py-1.5">Registration data</a>
                            </nav>
                        </SidebarInfoCard>
                    </aside>
                </div>{/* end grid */}
            </div>{/* end max-w container */}

            {openEditClient && isAdminRoute && ['admin', 'manager'].includes(user?.role) && (
                <EditClientModal
                    submission={s}
                    onClose={(updated) => {
                        setOpenEditClient(false);
                        if (updated) setS(updated);
                    }}
                />
            )}

            {openReqEdit && canRequestClientChange && (
                <ClientChangeRequestModal
                    key={s._id}
                    submission={s}
                    onClose={(updated) => {
                        setOpenReqEdit(false);
                        if (updated) setS(updated);
                    }}
                />
            )}

            {openAdminDelete && canAdminDeleteClient && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-900/50 backdrop-blur-sm" role="dialog" aria-modal="true">
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-xl ring-1 ring-ink-200 p-6 space-y-4">
                        <h3 className="text-lg font-extrabold text-ink-900">Delete client permanently</h3>
                        <p className="text-sm text-ink-600">
                            This removes <span className="font-semibold text-ink-900">{s.data?.name || 'this client'}</span> from the system.
                            Payments, LOI history, and linked incentive records will be deleted. This action cannot be undone.
                        </p>
                        <form onSubmit={deleteClientAsAdmin} className="space-y-3">
                            <label className="block">
                                <span className="ag-label">Reason for delete *</span>
                                <textarea
                                    className="ag-input min-h-[100px]"
                                    required
                                    value={adminDeleteReason}
                                    onChange={e => setAdminDeleteReason(e.target.value)}
                                    placeholder="Why is this client being removed?"
                                />
                            </label>
                            <div className="flex gap-2 justify-end pt-2">
                                <button
                                    type="button"
                                    className="ag-btn-ghost"
                                    onClick={() => { setOpenAdminDelete(false); setAdminDeleteReason(''); }}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="ag-btn-danger" disabled={adminDeleteBusy}>
                                    {adminDeleteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                    {adminDeleteBusy ? 'Deleting…' : 'Delete permanently'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {openReqDelete && canRequestClientChange && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-900/50 backdrop-blur-sm" role="dialog" aria-modal="true">
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-xl ring-1 ring-ink-200 p-6 space-y-4">
                        <h3 className="text-lg font-extrabold text-ink-900">Delete client</h3>
                        <p className="text-sm text-ink-600">
                            This sends a delete request to admin. The client is only cancelled after approval.
                        </p>
                        <form onSubmit={submitDeleteRequest} className="space-y-3">
                            <label className="block">
                                <span className="ag-label">Reason for delete *</span>
                                <textarea className="ag-input min-h-[100px]" required value={deleteReason} onChange={e => setDeleteReason(e.target.value)} />
                            </label>
                            <div className="flex gap-2 justify-end pt-2">
                                <button type="button" className="ag-btn-ghost" onClick={() => setOpenReqDelete(false)}>Cancel</button>
                                <button type="submit" className="ag-btn-danger" disabled={reqBusy}>
                                    {reqBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                    {reqBusy ? 'Sending…' : 'Send for approval'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ──────────────────────── Refunds block (F2) ─────────────────────────
 * Lists existing refund requests for this client and exposes a "Request
 * refund" button. The button is visible to the assigned sales team and
 * to admin/manager — and only when the client has approved payments left
 * to refund. */
function RefundsBlock({ s, user, isOwner, onChange }) {
    const [open, setOpen] = useState(false);
    const requests = s?.refundRequests || [];

    const isAdminish = ['admin', 'manager'].includes(user?.role);
    const isOnSalesTeam = (() => {
        const uid = String(user?._id || user?.id || '');
        return uid && [s?.salesTeam?.bda, s?.salesTeam?.bde, s?.salesTeam?.bdm]
            .map(x => String(x?._id || x || ''))
            .includes(uid);
    })();
    const canRequest = (isAdminish || isOwner || isOnSalesTeam)
        && s?.status === 'approved'
        && s?.clientStatus !== 'refunded';

    if (requests.length === 0 && !canRequest) return null;

    return (
        <SectionShell icon={RotateCcw} title="Refunds" subtitle="Refund requests on this client.">
            <div className="p-5 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="text-[11px] text-ink-500">{requests.length} request{requests.length === 1 ? '' : 's'}</span>
                    {canRequest && (
                        <button className="ag-btn-outline text-xs" onClick={() => setOpen(true)}>
                            <RotateCcw className="h-3.5 w-3.5" /> Request refund
                        </button>
                    )}
                </div>

                {requests.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead><tr className="text-[10px] uppercase tracking-wider text-ink-500">
                                <th className="text-left py-1">Requested</th>
                                <th className="text-left py-1">Reason</th>
                                <th className="text-left py-1">Mode</th>
                                <th className="text-right py-1">Amount</th>
                                <th className="text-left py-1">Status</th>
                                <th className="text-left py-1">By</th>
                            </tr></thead>
                            <tbody>
                                {requests.slice().reverse().map(r => (
                                    <tr key={r._id} className="border-t border-ink-100">
                                        <td className="py-1.5 text-xs text-ink-500">{new Date(r.requestedAt).toLocaleDateString('en-IN')}</td>
                                        <td className="py-1.5 text-xs">
                                            <div className="font-semibold">{r.reasonCode?.replace(/_/g, ' ')}</div>
                                            <div className="text-ink-500 truncate max-w-[260px]">{r.reason}</div>
                                        </td>
                                        <td className="py-1.5 text-xs">{r.refundMode?.replace(/_/g, ' ')}</td>
                                        <td className="py-1.5 text-right tabular-nums font-semibold">₹ {Number(r.amount).toLocaleString('en-IN')}</td>
                                        <td className="py-1.5">
                                            <span className={`ag-chip ${r.status === 'approved' ? 'bg-emerald-100 text-emerald-800'
                                                : r.status === 'rejected' ? 'bg-rose-100 text-rose-800'
                                                    : 'bg-amber-100 text-amber-800'}`}>
                                                {r.status}
                                            </span>
                                            {r.status === 'rejected' && r.rejectionReason && (
                                                <div className="text-[10px] italic text-rose-700 mt-0.5 max-w-[220px] truncate" title={r.rejectionReason}>"{r.rejectionReason}"</div>
                                            )}
                                        </td>
                                        <td className="py-1.5 text-xs">{r.requestedBy?.name || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {!canRequest && s?.clientStatus === 'refunded' && (
                    <div className="text-[11px] text-ink-500 inline-flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> This client is fully refunded — no further requests possible.
                    </div>
                )}

                {open && (
                    <RefundRequestModal
                        submission={s}
                        onClose={(refreshed) => {
                            setOpen(false);
                            if (refreshed) onChange();
                        }}
                    />
                )}
            </div>
        </SectionShell>
    );
}

/* ──────────────── Edit client modal (admin-only) ────────────────
 * Lets admin/manager fix the most-edited fields on a submission:
 * identity (name/phone/email), address (state/city), and the finance
 * inputs (dealAmount/tokenReceivedAmount/whiteAmount/isExempted/mode).
 * On Save, the backend recomputes the finance snapshot if any finance
 * input changed, and the pre-save hook refreshes totalReceived /
 * balanceAmount / receivedPercent automatically.
 *
 * Sales team, stage, and status have their own dedicated controls —
 * this modal stays focused on the data blob. */
function TdsRateEditor({ submission, snap, defaultTdsPercent, onSaved }) {
    const isExempted = !!snap?.isExempted;
    const resolvedRate = isExempted
        ? 0
        : Number(submission?.data?.tdsRate ?? snap?.tdsRate ?? defaultTdsPercent);
    const [rate, setRate] = useState(String(resolvedRate));
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setRate(String(isExempted ? 0 : resolvedRate));
    }, [isExempted, resolvedRate]);

    const save = async (e) => {
        e?.preventDefault();
        const next = Number(rate);
        if (!Number.isFinite(next) || next < 0 || next > 100) {
            toast.error('TDS rate must be between 0 and 100');
            return;
        }
        if (isExempted) {
            toast.error('Client is TDS-exempted — uncheck exemption in Edit client to apply a rate');
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.patch(`/forms/submissions/${submission._id}/data`, {
                patch: { tdsRate: next },
            });
            toast.success(data.recomputedFinance ? 'TDS rate updated — finance recomputed' : 'TDS rate updated');
            onSaved?.(data.submission);
        } catch { /* toasted */ }
        finally { setBusy(false); }
    };

    return (
        <form
            onSubmit={save}
            className="mx-5 mb-4 flex flex-col sm:flex-row sm:items-end gap-3 rounded-xl border border-brand-teal/20 bg-brand-teal-50/40 px-4 py-3"
        >
            <div className="flex-1 min-w-0">
                <label className="ag-label">TDS rate (%)</label>
                <div className="flex items-center gap-2">
                    <input
                        className="ag-input max-w-[120px] tabular-nums"
                        type="number"
                        min={0}
                        max={100}
                        step={0.01}
                        value={rate}
                        onChange={e => setRate(e.target.value)}
                        disabled={isExempted || busy}
                    />
                    <span className="text-sm font-semibold text-ink-500">%</span>
                </div>
                <p className="text-[11px] text-ink-500 mt-1">
                    {isExempted
                        ? 'TDS-exempted — rate is 0%. Edit client to change exemption.'
                        : `Default from Settings → Finance is ${defaultTdsPercent}%. Current TDS amount: ${inr(snap?.tdsAmount || 0)}.`}
                </p>
            </div>
            <button type="submit" className="ag-btn-primary shrink-0" disabled={busy || isExempted}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {busy ? 'Saving…' : 'Update TDS'}
            </button>
        </form>
    );
}

function EditClientModal({ submission, onClose }) {
    const finance = useSettings(st => st.finance);
    const d = submission?.data || {};
    const defaultTds = finance?.defaultTdsPercent ?? 2;
    const [f, setF] = useState({
        name: d.name || d.clientName || '',
        phone: d.phone || '',
        email: d.email || '',
        state: d.state || '',
        city: d.city || '',
        officeBranch: d.officeBranch || '',
        dealAmount: d.dealAmount ?? '',
        tokenReceivedAmount: d.tokenReceivedAmount ?? '',
        whiteAmount: d.whiteAmount ?? '',
        isExempted: !!d.isExempted,
        tdsRate: d.tdsRate != null && d.tdsRate !== '' ? d.tdsRate : defaultTds,
        modeOfPayment: d.modeOfPayment || '',
        leadSource: d.leadSource || '',
        remark: d.remark || '',
    });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

    const submit = async (e) => {
        e?.preventDefault();
        setBusy(true);
        try {
            const patch = {};
            /* Send only fields the admin actually changed so the audit row
               is meaningful and we don't accidentally overwrite a value
               the user reset to empty. */
            for (const [k, v] of Object.entries(f)) {
                const orig = d[k];
                const normOrig = orig === undefined || orig === null ? '' : orig;
                const normNext = v === undefined || v === null ? '' : v;
                if (String(normOrig) !== String(normNext)) patch[k] = v;
            }
            if (Object.keys(patch).length === 0) {
                toast('No changes to save');
                onClose(null);
                return;
            }
            if ('tdsRate' in patch) {
                const tds = Number(patch.tdsRate);
                if (!Number.isFinite(tds) || tds < 0 || tds > 100) {
                    toast.error('TDS rate must be between 0 and 100');
                    return;
                }
                patch.tdsRate = f.isExempted ? 0 : tds;
            }
            const { data } = await api.patch(`/forms/submissions/${submission._id}/data`, { patch });
            toast.success(data.recomputedFinance
                ? 'Saved — finance recomputed'
                : 'Saved');
            onClose(data.submission || null);
        } catch {/* toasted */ }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-900/50 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="edit-client-title">
            <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl ring-1 ring-ink-200 max-h-[90vh] flex flex-col">
                <div className="px-5 py-4 border-b border-ink-100 flex items-center justify-between shrink-0">
                    <div>
                        <h3 id="edit-client-title" className="font-extrabold">Edit client</h3>
                        <p className="text-[11px] text-ink-500 mt-0.5">
                            Admin-only. Changing the deal amount or token automatically recomputes GST, TDS, and final payable.
                        </p>
                    </div>
                    <button type="button" className="text-ink-500 hover:text-brand-red" onClick={() => onClose(null)} aria-label="Close">
                        <XIcon className="h-4 w-4" />
                    </button>
                </div>

                <form onSubmit={submit} className="p-5 space-y-4 overflow-y-auto">
                    <Group label="Identity">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <TF label="Client name *" value={f.name} onChange={v => set('name', v)} required />
                            <TF label="Phone *" value={f.phone} onChange={v => set('phone', v)} required />
                            <TF label="Email" value={f.email} type="email" onChange={v => set('email', v)} />
                        </div>
                    </Group>

                    <Group label="Address & office">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <TF label="State" value={f.state} onChange={v => set('state', v)} />
                            <TF label="City" value={f.city} onChange={v => set('city', v)} />
                            <TF label="Office branch" value={f.officeBranch} onChange={v => set('officeBranch', v)} />
                        </div>
                    </Group>

                    <Group label="Finance inputs">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <TF label="Deal amount (₹) *" type="number" value={f.dealAmount}
                                onChange={v => set('dealAmount', v === '' ? '' : Number(v))} required />
                            <TF label="Token received (₹)" type="number" value={f.tokenReceivedAmount}
                                onChange={v => set('tokenReceivedAmount', v === '' ? '' : Number(v))} />
                            <TF label="White amount (₹) — cash deals only" type="number" value={f.whiteAmount}
                                onChange={v => set('whiteAmount', v === '' ? '' : Number(v))} />
                            <TF
                                label="TDS rate (%)"
                                type="number"
                                value={f.isExempted ? 0 : f.tdsRate}
                                onChange={v => set('tdsRate', v === '' ? '' : Number(v))}
                                placeholder={String(defaultTds)}
                            />
                            <div>
                                <label className="ag-label">Payment mode</label>
                                <select className="ag-input" value={f.modeOfPayment} onChange={e => set('modeOfPayment', e.target.value)}>
                                    <option value="">—</option>
                                    {['upi', 'bank_transfer', 'cheque', 'cash'].map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </div>
                        </div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer mt-1">
                            <input
                                type="checkbox"
                                checked={f.isExempted}
                                onChange={e => {
                                    const checked = e.target.checked;
                                    setF(prev => ({
                                        ...prev,
                                        isExempted: checked,
                                        tdsRate: checked ? 0 : (prev.tdsRate || defaultTds),
                                    }));
                                }}
                            />
                            TDS-exempted client
                        </label>
                        <p className="text-[11px] text-ink-500 mt-1">
                            Deal amount, TDS %, or exemption changes recompute GST, TDS amount, and final payable on save.
                            Default TDS from Settings → Finance is {defaultTds}%.
                        </p>
                    </Group>

                    <Group label="Source & notes">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="ag-label">Lead source</label>
                                <select className="ag-input" value={f.leadSource} onChange={e => set('leadSource', e.target.value)}>
                                    <option value="">—</option>
                                    {['crm', 'kustard', 'affairsStudio', 'other'].map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </div>
                            <TF label="Remarks" value={f.remark} onChange={v => set('remark', v)} />
                        </div>
                    </Group>

                    <div className="flex justify-end gap-2 pt-2 border-t border-ink-100">
                        <button type="button" className="ag-btn-ghost" onClick={() => onClose(null)}>Cancel</button>
                        <button type="submit" className="ag-btn-primary" disabled={busy}>
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {busy ? 'Saving…' : 'Save changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Group({ label, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold mb-2">{label}</div>
            <div className="rounded-xl ring-1 ring-ink-100 p-3 bg-ink-50/40">{children}</div>
        </div>
    );
}
function TF({ label, value, onChange, type = 'text', required, placeholder }) {
    return (
        <div>
            <label className="ag-label">{label}</label>
            <input
                className="ag-input"
                type={type}
                value={value ?? ''}
                required={required}
                placeholder={placeholder}
                onChange={e => onChange(e.target.value)}
            />
        </div>
    );
}
